/**
 * Storage hierarchy — the client half of a domain dictionary that is
 * duplicated by design (see the root CLAUDE.md). The server half is
 * `StorageNodeKind` / `StorageNode` in `cgt-backend/prisma/schema.prisma`;
 * changing one side without the other silently desynchronises the app.
 */

/**
 * What a node *is*. A label for humans and for ordering — NOT a per-level
 * schema. The tree does not enforce a fixed ladder (a shipper is UNIT →
 * POSITION with nothing between). The one kind carrying a rule is POSITION: it
 * is the only kind that may be occupied.
 */
export const STORAGE_NODE_KINDS = [
  'SITE',
  'FACILITY',
  'BUILDING',
  'ROOM',
  'UNIT',
  'ZONE',
  'RACK',
  'BOX',
  'POSITION',
] as const

export type StorageNodeKind = (typeof STORAGE_NODE_KINDS)[number]

export const STORAGE_NODE_KIND_LABELS: Record<StorageNodeKind, string> = {
  SITE: 'Site',
  FACILITY: 'Facility',
  BUILDING: 'Building',
  ROOM: 'Room',
  UNIT: 'Unit',
  ZONE: 'Zone',
  RACK: 'Rack',
  BOX: 'Box',
  POSITION: 'Position',
}

/** The only kind that holds stock. Mirrors `OCCUPIABLE_KIND` on the server. */
export const OCCUPIABLE_KIND: StorageNodeKind = 'POSITION'

/**
 * How a declared grid labels its cells. Mirrors `SlotLabelScheme` in
 * `cgt-backend/prisma/schema.prisma`. ROW_COLUMN is the ANSI/SLAS cryobox
 * convention (A1…D5); SEQUENTIAL is one prefix plus a running row-major index
 * (R01…R10), which is what the design note's own addresses use.
 */
export type SlotLabelScheme = 'ROW_COLUMN' | 'SEQUENTIAL'

/**
 * A storage condition CODE (design note §5.2). Conditions are per tenant and
 * editable (Inventory → Configuration → Storage Profile → Storage conditions),
 * so any string the tenant created is one; `BuiltInCondition` lists the ones
 * every tenant starts with. Labels, temperatures and families are NOT
 * duplicated here — they come from `GET /inventory/storage-nodes/conditions`.
 */
export type StorageCondition = string

/** The conditions every tenant starts with. Mirrors the backend's built-in table. */
export type BuiltInCondition =
  | 'VAPOR_PHASE_LN2'
  | 'LN2_IMMERSION'
  | 'ULTRA_LOW_MECHANICAL'
  | 'FREEZER_MINUS_40'
  | 'FREEZER_MINUS_20'
  | 'REFRIGERATED'
  | 'CONTROLLED_COOL'
  | 'ROOM_TEMP'
  | 'CONTROLLED_ROOM_TEMP'
  | 'CONTROLLED_RATE_FREEZER'

/** The temperature bands the conditions group into. */
export type TemperatureFamily = 'CRYOGENIC' | 'ULTRA_LOW' | 'FREEZER' | 'REFRIGERATED' | 'AMBIENT_CRT'

export const TEMPERATURE_FAMILIES: TemperatureFamily[] = [
  'CRYOGENIC',
  'ULTRA_LOW',
  'FREEZER',
  'REFRIGERATED',
  'AMBIENT_CRT',
]

/** One of the tenant's conditions, as `/conditions` and `/inventory/storage-conditions` serve it. */
export type StorageConditionMeta = {
  /** The row id — what edits and deletes address. */
  id: string
  /** The code locations, materials and mappings store. Never changes. */
  name: StorageCondition
  label: string
  temperature: string
  family: TemperatureFamily | null
  usedFor: string
  /** False for the controlled-rate freezer: product passes through, never rests. */
  persistent: boolean
  /** Off: no longer offered for new choices; what already uses it keeps working. */
  active: boolean
  builtIn: boolean
  order: number
  updatedBy?: string
  updatedAt?: string
}

export type StorageConditionInput = {
  label: string
  temperature?: string
  family: TemperatureFamily
  usedFor?: string
  persistent?: boolean
}

export type StorageConditionPatch = Partial<StorageConditionInput> & {
  active?: boolean
  order?: number
}

export type TemperatureFamilyMeta = {
  name: TemperatureFamily
  label: string
  range: string
  usedFor: string
}

export type StorageConditionsReference = {
  conditions: StorageConditionMeta[]
  families: TemperatureFamilyMeta[]
}

/** Separator used in the materialised path. Not legal inside a node code. */
export const PATH_SEPARATOR = '/'

/** A storage node as returned by `GET /inventory/storage-nodes`. */
export type ApiStorageNode = {
  id: string
  parentId: string | null
  /** Set only on a root: the Purpose=Storage unit the tree hangs from. */
  equipmentId: string | null
  kind: StorageNodeKind
  /** Address within the parent ("R3", "A1") — the full address is `path`. */
  code: string
  name: string | null
  /** Materialised root→leaf path, e.g. "LN2-A/R1/B1/A1". */
  path: string
  depth: number
  gridRows: number | null
  gridColumns: number | null
  /** What this node's declared grid mints (a freezer's grid OF RACKS, …). */
  gridChildKind: StorageNodeKind | null
  labelScheme: SlotLabelScheme | null
  labelPrefix: string | null
  /**
   * This node's 1-based coordinates within its parent's declared grid.
   * Structural identity — the slot map places cells from these, never by
   * re-parsing the label.
   */
  gridRow: number | null
  gridColumn: number | null
  /**
   * The validated condition declared ON this node. Null is the common case —
   * most nodes inherit from the unit above them, so read the ancestor chain
   * rather than treating null as "no condition".
   */
  providesCondition: StorageCondition | null
  /**
   * The inventory Storage Profile `providesCondition` above was derived from.
   *
   * Null means the condition was set directly — by the seed, an import, or the
   * API — or not at all. The profile is where the condition is configured, so
   * this is what the Equipment → Storage control edits.
   */
  storageProfileId: string | null
  /**
   * The picking zone declared ON this node, inherited downwards like the
   * condition. A zone is a WALK — which trip a picker makes and in what order —
   * not a temperature.
   */
  zoneId: string | null
  blocked: boolean
  active: boolean
  /**
   * The one position per migrated zone holding stock whose free-text location
   * code could not be parsed. Excluded from every capacity figure — it is not
   * a physical slot.
   */
  isHoldingArea: boolean
  migratedFromZoneId: string | null
  /**
   * Whether this position currently holds stock. Present only on the subtree
   * endpoint, which resolves it server-side in one grouped query — the slot map
   * cannot distinguish free from occupied without it.
   */
  occupied?: boolean
  /**
   * True when this cell has no row of its own and is described by its parent's
   * grid declaration.
   *
   * A box's hundred slots are named by four columns on the box, so storing a
   * row for each one records the same fact twice — and that second copy is what
   * made a million-slot freezer 325 MB and a 79-second rename. The server
   * derives these cells on read and gives them a row the first time something
   * makes one different: stock lands in it, it is blocked, or it is given its
   * own condition.
   *
   * It needs no special handling here. A derived cell arrives in the same shape
   * as a stored one and its `id` is accepted anywhere a node id is, so a
   * put-away or a block sends it back unchanged and the server resolves it. The
   * flag is for display only — "never been touched" — and is absent on units
   * built before this, where every cell is a row.
   */
  virtual?: boolean
}

/**
 * A unit's free slots, from `GET …/:id/free-slots` (z8nrz7fqfy).
 *
 * `nodes` is the first page of free slots — declared ones included, by their
 * virtual id — preceded by the containers above them, so the helpers that
 * worked on a subtree (`freePositions`, `slotOptionsFor`, …) work on it
 * unchanged. Every slot carries `occupied: false` except one passed as
 * `include`.
 */
export type FreeSlots = {
  nodes: ApiStorageNode[]
  /** Free slots in all; without a condition, the unit's capacity `remaining`. */
  freeTotal: number
  /** The unit's slot count. 0 means none are described yet — not full. */
  declared: number
  /** True when `nodes` holds fewer slots than `freeTotal`. */
  truncated: boolean
}

/** What compacting a unit would remove, from `GET …/:id/compactable`. */
export type CompactionSummary = {
  nodeId: string
  path: string
  /** Cell rows the declarations above them already describe exactly. */
  removable: number
  /** Position rows kept because they record something a declaration cannot. */
  kept: number
}

/** The result of `POST …/:id/compact`. */
export type CompactionResult = {
  nodeId: string
  removed: number
  kept: number
}

/**
 * A container in the hierarchy, carrying its own capacity.
 *
 * The tree endpoint sends containers only — the pane lists racks and boxes,
 * never slots — and the fill figures the browser used to add up from a full
 * subtree come pre-computed. That is what takes opening a large freezer from a
 * 29 MB response to under one.
 */
export type StorageTreeNode = ApiStorageNode & {
  capacity?: StorageCapacity
  /**
   * How many containers sit directly inside this one, whether or not they were
   * sent.
   *
   * The pane loads a level at a time, so it cannot learn the shape of the unit
   * by looking at what it holds: an unopened rack holds nothing here and would
   * draw as a leaf with no way to open it. This is also what makes the decision
   * to open the next level automatically — a dewar of four racks should arrive
   * fully open, a freezer of a hundred must not, and the difference is a number
   * you need BEFORE fetching.
   *
   * Slots are not counted. They are drawn on the slot map, and most of them
   * have no row to count.
   */
  childCount?: number
}

/** `GET /inventory/storage-nodes/:id/tree` — the hierarchy, without the slots. */
export type StorageTree = {
  nodeId: string
  path: string
  /**
   * How many containers the unit holds, as against how many were sent.
   *
   * The two differ whenever the tree is read a level at a time, and the pane
   * needs the first: it offers to open everything only below a row budget, and
   * judging that by what it is holding would offer it on a freezer of ten
   * thousand boxes that had loaded a hundred racks.
   */
  containerCount: number
  containers: StorageTreeNode[]
}

/** One container's grid and every cell in it, stored and derived alike. */
export type GridCells = {
  nodeId: string
  path: string
  gridRows: number | null
  gridColumns: number | null
  gridChildKind: StorageNodeKind | null
  labelScheme: SlotLabelScheme | null
  labelPrefix: string | null
  cells: ApiStorageNode[]
}

/**
 * Derived capacity from `GET /inventory/storage-nodes/:id/capacity`.
 * Every number here is computed on read; none is stored, and `remaining` can
 * never be typed in.
 */
export type StorageCapacity = {
  declared: number
  blocked: number
  spaces: number
  used: number
  remaining: number
  full: boolean
}

/** A free-text location code the migration parked rather than guessed at. */
export type ApiUnresolvedLocation = {
  id: string
  entityType: 'InventoryLot' | 'PickListLine' | string
  entityId: string
  originalZoneId: string | null
  originalLocationCode: string | null
  reason: string
  resolvedAt: string | null
  resolvedNodeId: string | null
  createdAt: string
}

/** What one re-resolve pass over the parked codes managed to do. */
export type ReresolveSummary = {
  considered: number
  /** Lots whose code matched a position, put away through the normal checks. */
  placed: number
  /** Pick-list lines marked reviewed — a printed sheet is not re-addressed. */
  reviewed: number
  /** Still needs a human: no match, ambiguous, or the put-away was refused. */
  stillParked: number
  refused: { locationCode: string | null; reason: string }[]
}

/**
 * What deleting a node and its layout would remove, and what is in the way.
 *
 * Fetched before the confirmation so it can state the size of the thing:
 * "delete 250 nodes" is a decision, "delete" is not.
 */
export type DeletableSummary = {
  nodes: number
  /** How many of each kind, e.g. { UNIT: 1, RACK: 8, POSITION: 216 }. */
  byKind: Record<string, number>
  /** Lots stored anywhere beneath it. Any at all blocks the delete. */
  occupants: number
  pickLines: number
  canDelete: boolean
}

/** What a slot shows on the map. Free, occupied and blocked are distinct. */
export type SlotState = 'free' | 'occupied' | 'blocked'

/** One row of `GET /inventory/storage-nodes/capacity-summary` (Figure 9). */
/**
 * Why a storage unit cannot take stock, or null when it can.
 *
 * Mirrors the backend `UnitUnavailability` in
 * cgt-backend/src/inventory/storage/unit-availability.ts, where it is DERIVED
 * from equipment status plus any STARTED maintenance event and never stored.
 *
 * `detail` is the backend's own wording and is rendered verbatim by the banner,
 * so the refusal a user hits on put-away and the explanation they read on the
 * unit page cannot drift apart.
 */
export type UnitUnavailability = {
  reason: 'OUT_OF_SERVICE' | 'MAINTENANCE_IN_PROGRESS'
  detail: string
} | null

export type CapacitySummaryRow = {
  nodeId: string
  code: string
  name: string | null
  kind: StorageNodeKind
  equipmentId: string | null
  /** "5×2" from the root's own grid, or 'nested' when capacity lives deeper. */
  layout: string
  /** Figure 9's storage profile: the condition this unit is operated at. */
  condition: StorageCondition | null
  family: TemperatureFamily | null
  declared: number
  blocked: number
  spaces: number
  used: number
  remaining: number
  full: boolean
  unavailable: UnitUnavailability
}

/** One hit from `GET /inventory/storage-nodes/locate?q=`. */
export type LocateResult = {
  lotId: string
  lotNumber: string
  quantity: number
  materialCode: string
  materialName: string
  nodeId: string
  path: string
}

/**
 * One row of the put-away queue (`GET /inventory/storage-nodes/unplaced`):
 * stock that has arrived but has not been shelved.
 */
export type UnplacedLot = {
  lotId: string
  lotNumber: string
  quantity: number
  materialCode: string
  materialName: string
  /** What it must be kept at; null means the master data does not say. */
  requiresCondition: StorageCondition | null
  expiryDate: string | null
}

/** A therapy product held in a slot — the other kind of occupant. */
export type ContentsProduct = {
  productId: string
  din: string
  productDescriptionCode: string
  patientName: string | null
  patientMrn: string | null
  nodeId: string
  path: string
}

/** `GET /inventory/storage-nodes/:id/contents`. */
export type ContentsResult = {
  nodeId: string
  path: string
  lots: LocateResult[]
  /** Optional so a backend without the products column still parses; read with `?? []`. */
  products?: ContentsProduct[]
}

/**
 * `GET /inventory/storage-nodes/free-space`.
 *
 * `conditionsChecked` is false unless a required condition was supplied, and
 * the UI must say which it is: "these have room" and "these have room AND are
 * the right temperature" are very different claims to put in front of someone
 * deciding where a patient's product goes.
 */
export type FreeSpaceResult = {
  conditionsChecked: boolean
  requires: StorageCondition | null
  units: CapacitySummaryRow[]
}

/**
 * One row of a storage audit trail.
 *
 * `eventType` is a free-text string on the wire, not an enum — the server's
 * vocabulary is documented on `StorageNodeAudit` and may grow, so the UI must
 * render an unrecognised value rather than crash on it.
 *
 * `oldValue` / `newValue` are whatever JSON the event carried, which differs
 * per event type. Read them through `describeStorageAuditEvent` rather than
 * reaching into them from a component.
 */
export type StorageAuditEntry = {
  id: string
  nodeId: string
  /** Set only on events that moved a lot (`occupied` / `vacated`). */
  lotId: string | null
  /** The operator's stated reason. Only destructive/blocking acts carry one. */
  reason: string | null
  eventType: string
  oldValue: unknown
  newValue: unknown
  /**
   * The actor's Cognito `sub` — an id, not a display name. This is the record:
   * it is what the append-only row stores and is never rewritten.
   */
  changedBy: string
  /**
   * `changedBy` resolved to a person, for display only. `name` is null when the
   * sub matches no user (a deleted account, or an actor from another system) —
   * fall back to `changedBy` rather than hiding the row.
   */
  changedByActor: ResolvedActor
  changedAt: string
}

/** An audit actor resolved for display. `id` is always the raw `changedBy`. */
export type ResolvedActor = {
  id: string
  name: string | null
  email: string | null
}

/** One row of a zone audit trail. Same shape, keyed by zone instead of node. */
export type ZoneAuditEntry = Omit<StorageAuditEntry, 'nodeId' | 'lotId'> & {
  zoneId: string
}

/** A page of audit rows. The trail only grows, so reads are always paged. */
export type AuditPage<T> = {
  items: T[]
  total: number
  skip: number
  take: number
}

// ─── Copying a unit's layout (ClickUp z8nrz7dq5k) ───────────────────────────

/**
 * One level of a layout, in the shape the build endpoint accepts.
 *
 * Mirrors `LayoutLevelDto`. A level is EITHER named (`codes`) or counted
 * (`rows`×`columns`) — never both, never neither.
 */
export type LayoutLevel = {
  kind: StorageNodeKind
  codes?: string[]
  rows?: number
  columns?: number
  scheme?: SlotLabelScheme
  prefix?: string
}

/**
 * An existing unit described as a layout, from
 * `GET /inventory/storage-nodes/:id/layout-template`.
 *
 * `uniform: false` means the source could not be described exactly — a level
 * applies to every parent above it, so racks that disagree cannot round-trip.
 * The dominant shape comes back anyway and `notes` says what differs, because
 * a near-match still saves the work and the person is about to edit it.
 */
export type LayoutTemplate = {
  sourceId: string
  sourceCode: string
  sourceName: string | null
  levels: LayoutLevel[]
  uniform: boolean
  notes: string[]
  /** Blocked slots, as addresses relative to the unit ("R01/C03/A5"). */
  blockedCells: string[]
  providesCondition: StorageCondition | null
  storageProfileId: string | null
}

/** What the person chose to bring across from the source. */
export type CopyLayoutChoice = {
  /** Always true — the structure is the point of copying. */
  structure: true
  condition: boolean
  blockedCells: boolean
}

// ─── Storage transfer planning (ClickUp z8nrz7dq5k) ─────────────────────────
//
// Mirrors cgt-backend/prisma/schema.prisma and storage-transfers.service.ts.
// Duplicated by design and desyncs SILENTLY — change one side, change this one
// in the same commit.

/**
 * What becomes of the SOURCE unit once the transfer completes.
 *
 * An enum, while the reason beside it is a tenant-extensible catalog, for the
 * reason the schema states: code branches on this, and code cannot branch on a
 * name a tenant invented.
 */
export type StorageTransferDisposition =
  | 'DECOMMISSION'
  | 'RETURN_TO_SERVICE'
  | 'REMAINS_IN_SERVICE'

export type StorageTransferStatus = 'DRAFT' | 'READY' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'

export type StorageTransferMoveStatus = 'PENDING' | 'MOVED' | 'SKIPPED'

/** A row of the tenant's reason catalog. */
export type StorageTransferReason = {
  id: string
  code: string
  label: string
  /** The wizard's default disposition for this reason. Not binding. */
  disposition: StorageTransferDisposition
  order: number
  /** A seeded standard reason: renamable, undeletable, code immutable. */
  isSystem: boolean
}

/**
 * The seven standard reasons, mirrored from
 * cgt-backend/src/inventory/storage/transfer-reasons.ts so the wizard's first
 * dropdown can render before the catalog request lands. Codes and labels MUST
 * stay in step with that file.
 */
export const STANDARD_TRANSFER_REASONS: Array<Omit<StorageTransferReason, 'id'>> = [
  { code: 'planned_maintenance', label: 'Planned maintenance (PM)', disposition: 'RETURN_TO_SERVICE', order: 0, isSystem: true },
  { code: 'corrective_repair', label: 'Repair', disposition: 'RETURN_TO_SERVICE', order: 1, isSystem: true },
  { code: 'decontamination', label: 'Decontamination / defrost', disposition: 'RETURN_TO_SERVICE', order: 2, isSystem: true },
  { code: 'requalification', label: 'Requalification (IQ/OQ/PQ)', disposition: 'RETURN_TO_SERVICE', order: 3, isSystem: true },
  { code: 'relocation', label: 'Relocation', disposition: 'RETURN_TO_SERVICE', order: 4, isSystem: true },
  { code: 'end_of_life', label: 'End of life / retirement', disposition: 'DECOMMISSION', order: 5, isSystem: true },
  { code: 'capacity_rebalance', label: 'Capacity rebalancing', disposition: 'REMAINS_IN_SERVICE', order: 6, isSystem: true },
]

/**
 * What a move carries, in words a person can check against a label. Mirrors
 * `MoveItem` in cgt-backend/src/inventory/storage/transfer-move-item.ts.
 * Patient fields are deliberately absent — they are PHI.
 */
export type MoveItem = {
  kind: 'LOT' | 'PRODUCT'
  /** One line naming the item: "DIN code" for a product, "material · Lot n" for a lot. */
  label: string
  din: string | null
  productDescriptionCode: string | null
  /** The catalog entry's product class, e.g. "HPC, APHERESIS". */
  productName: string | null
  lotNumber: string | null
  materialName: string | null
  quantity: number | null
  /** ISO timestamp. */
  expiryDate: string | null
  /**
   * What the item's own label encodes: `<material>|LOT-<lot>` for a lot, the
   * DIN for a product. Shown under the scan step so the tech knows which label
   * to look for. The server does the matching.
   */
  scanCode?: string | null
}

/** One occupant's planned journey out of a source position into a target one. */
export type StorageTransferMove = {
  id: string
  transferId: string
  /** Walk order, so the plan reads the way someone works through the unit. */
  ordinal: number
  /** Exactly one of these is set. */
  lotId: string | null
  productId: string | null
  sourceNodeId: string
  /** Snapshot of the source address at planning time, not a live lookup. */
  sourceNodePath: string
  /**
   * A node id, or a `v:<parentId>:<row>:<col>` declared-cell id. Deliberately
   * not a foreign key — a plan's destinations are empty slots in a sparse
   * tree, and materialising thousands of rows for a plan that may be cancelled
   * would defeat the sparse model for the whole target unit.
   */
  targetNodeRef: string | null
  targetNodePath: string | null
  status: StorageTransferMoveStatus
  /** 'ok', or the refusal text from the shared put-away check. */
  conditionVerdict: string
  skipReason: string | null
  /**
   * What was scanned when the move was carried out (z8nrz7e7ak). Set on a
   * MOVED move. `scannedAt` / `scannedBy` are also set on a move skipped at
   * the freezer, which is what tells it apart from one excluded while the plan
   * was a draft.
   */
  scannedSourceCode?: string | null
  scannedItemCode?: string | null
  scannedTargetCode?: string | null
  scannedAt?: string | null
  scannedBy?: string | null
  /** Null when the lot or product has been deleted since planning. */
  item: MoveItem | null
}

/** The three scans that carry out one move, in the order they are made. */
export type TransferScanStep = 'source' | 'item' | 'target'

/** The server's answer to one scan. */
export type TransferScanVerdict = {
  ok: boolean
  step: TransferScanStep
  /** The code the step wanted, in the form its label prints. */
  expected: string | null
  /** What was scanned, trimmed. */
  scanned: string
  /** Why the scan failed, for the person holding the phone. Null on a pass. */
  message: string | null
}

export type StorageTransfer = {
  id: string
  sourceEquipmentId: string
  targetEquipmentId: string
  sourceRootId: string
  targetRootId: string
  reasonCode: string
  reasonNote: string | null
  disposition: StorageTransferDisposition
  maintenanceEventId: string | null
  /** Set on a RETURN LEG, naming the outbound transfer it brings home. */
  returnOfTransferId: string | null
  status: StorageTransferStatus
  assigneeId: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  readyAt: string | null
  cancelledAt: string | null
  reason?: StorageTransferReason
  /**
   * The two units, by code. Sent on every read of a transfer — a plan that
   * carries only root ids cannot say "FRZ-01 → FRZ-02" without fetching every
   * storage root and matching ids, which is what the detail page used to do.
   */
  sourceRoot?: StorageTransferEnd
  targetRoot?: StorageTransferEnd
  _count?: { moves: number }
}

/** One end of a transfer, as the API names it. */
export type StorageTransferEnd = {
  id: string
  code: string
  name: string | null
}

export type StorageTransferDetail = StorageTransfer & { moves: StorageTransferMove[] }

/** One planned move as `preview` computes it, before anything is persisted. */
export type PlannedMove = {
  ordinal: number
  lotId: string | null
  productId: string | null
  sourceNodeId: string
  sourceNodePath: string
  targetNodeRef: string | null
  targetNodePath: string | null
  conditionVerdict: string
  /** True when no destination could be found at all. */
  unmapped: boolean
  item: MoveItem | null
}

export type TransferPreview = {
  moves: PlannedMove[]
  /** Occupants that found no destination. Zero when the plan is complete. */
  capacityShortfall: number
  sourceRootId: string
  sourceRootPath: string
  targetRootId: string
  targetRootPath: string
}

/** A replacement unit, with what the wizard needs to choose between them. */
export type TransferCandidate = CapacitySummaryRow & {
  occupants: number
  fits: boolean
  shortfall: number
  conditionMatches: boolean
}

/**
 * The replacement units on offer, and what the server withheld.
 *
 * A unit at an incompatible storage condition never reaches `units` — matching
 * is exact, so an offered mismatch would be a product loss rather than a
 * trade-off. `excludedForCondition` exists so the UI can SAY that, instead of
 * a freezer somebody knows is empty silently not being listed.
 */
export type CandidateResult = {
  requires: StorageCondition | null
  /** False when the source declares no condition — then only room was checked. */
  conditionsChecked: boolean
  units: TransferCandidate[]
  excludedForCondition: number
}

/** What has drifted under a plan since it was drawn. */
export type RevalidationDiff = {
  /** True while the plan is a DRAFT, when the drift is also written back. */
  applied: boolean
  goneOccupants: string[]
  newOccupants: number
  takenDestinations: string[]
}
